import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, UserRole, ValidationActionType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField, ValidationRule } from 'models/ui';
import { Locales } from 'constants/app.constant';
import { assignmentHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import DoNotResuscitateView, { DoNotResuscitateViewProps } from './DoNotResuscitateView';
import { DecisionCapacityValue, FormField, FormFieldLabel, NON_REQUIRED_FIELDS, PATIENT_CONSENT_SIGNATURE_FIELDS, REQUIRED_DECISION_MAKER_FIELDS, SectionTitle } from './constants';

const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;
  return chartService.buildPlainRecord({
    formField: FormField.PROVIDER_NURSE,
    title: FormFieldLabel.PROVIDER_NURSE,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

interface DoNotResuscitateProps extends ChartComponentProps {
  navigateToSavedPatientCharting: VoidFunction;
  authorName?: string;
  userRole?: string;
  locale?: Locales;
}

class DoNotResuscitate extends Component<DoNotResuscitateProps> {
  static displayName = 'DoNotResuscitate';

  getDecisionCapacityOptions = (selectedValue: string) => {
    const normalizedValue = String(selectedValue || '').trim().toLowerCase();
    return [
      { id: DecisionCapacityValue.YES, label: 'Yes', value: DecisionCapacityValue.YES, selected: normalizedValue === DecisionCapacityValue.YES },
      { id: DecisionCapacityValue.NO, label: 'No', value: DecisionCapacityValue.NO, selected: normalizedValue === DecisionCapacityValue.NO }
    ] as any;
  };

  componentDidMount() {
    this.props.initState(this.buildFormFields());
  }

  createField = ({ name, type, label, value = '' }: { name: FormField; type: FormFieldInputType; label?: string; value?: string }): ChartMetaFormField => {
    return chartHelper.createFormField({ name, type, label, value });
  };

  buildFormFields = (chartData: Partial<Record<FormField, string>> = {}): Map<string, ChartMetaFormField> => {
    const dataMap = new Map<string, ChartMetaFormField>();

    const addField = (name: FormField, type: FormFieldInputType, label?: string) => {
      dataMap.set(name, this.createField({ name, type, label, value: chartData[name] || '' }));
    };

    addField(FormField.STATE, FormFieldInputType.MULTI_SELECT_RADIO, FormFieldLabel.STATE);

    addField(FormField.PATIENT_FULL_NAME, FormFieldInputType.TEXT_BOX, FormFieldLabel.PATIENT_FULL_NAME);

    const decisionCapacityField = this.createField({
      name: FormField.DECISION_CAPACITY,
      type: FormFieldInputType.RADIO_CHOICE,
      label: FormFieldLabel.DECISION_CAPACITY,
      value: chartData[FormField.DECISION_CAPACITY] || ''
    });
    decisionCapacityField.chartContent = this.getDecisionCapacityOptions(chartData[FormField.DECISION_CAPACITY] || '');
    dataMap.set(FormField.DECISION_CAPACITY, decisionCapacityField);

    addField(FormField.DECISION_MAKER, FormFieldInputType.DROPDOWN, FormFieldLabel.DECISION_MAKER);
    addField(FormField.DECISION_MAKER_NAME, FormFieldInputType.TEXT_BOX, FormFieldLabel.DECISION_MAKER_NAME);
    addField(FormField.RELATIONSHIP_TO_PATIENT, FormFieldInputType.TEXT_BOX, FormFieldLabel.RELATIONSHIP_TO_PATIENT);

    addField(FormField.PATIENT_CONSENT_CHECKED, FormFieldInputType.CHECK_BOX);
    addField(FormField.PATIENT_CONSENT_SIGNATURE, FormFieldInputType.TEXT_BOX, FormFieldLabel.PATIENT_CONSENT_SIGNATURE);

    addField(FormField.PROVIDER_ORDER_CHECKED, FormFieldInputType.CHECK_BOX);
    addField(FormField.PROVIDER_NAME, FormFieldInputType.TEXT_BOX, FormFieldLabel.PROVIDER_NAME);
    addField(FormField.PROVIDER_TYPE, FormFieldInputType.DROPDOWN, FormFieldLabel.PROVIDER_TYPE);
    addField(FormField.EMERGENCY_PHONE, FormFieldInputType.TEXT_BOX, FormFieldLabel.EMERGENCY_PHONE);
    addField(FormField.PROVIDER_SIGNATURE, FormFieldInputType.TEXT_BOX, FormFieldLabel.PROVIDER_SIGNATURE);

    return dataMap;
  };

  getDecisionCapacityValue = (): string => {
    const field = this.props.formFieldMap.get(FormField.DECISION_CAPACITY);
    return chartService.buildRecordFromField(field)?.value || '';
  };

  shouldShowDecisionMakerFields = (): boolean => this.getDecisionCapacityValue() === DecisionCapacityValue.NO;

  shouldShowPatientConsent = (): boolean => Boolean(this.getDecisionCapacityValue());

  getConditionalRequiredFieldNames = (): string[] => [
    ...(this.shouldShowDecisionMakerFields() ? REQUIRED_DECISION_MAKER_FIELDS : []),
    ...(this.shouldShowPatientConsent() ? [FormField.PATIENT_CONSENT_CHECKED] : []),
    FormField.PROVIDER_ORDER_CHECKED
  ];

  getRequiredFieldNames = (): string[] => {
    const requiredRules: ValidationRule[] = (this.props.chartMetadata?.chartFieldValidationSet || []).filter(
      (rule) => rule.validationActionType === ValidationActionType.REQUIRED
    );

    const hidePatientConsentSignature = !this.shouldShowPatientConsent();

    const metadataRequiredFieldNames = Array.from(new Set(requiredRules.map((rule) => rule.formFieldId).filter(Boolean))).filter(
      (fieldName) => !NON_REQUIRED_FIELDS.includes(fieldName) && !(hidePatientConsentSignature && PATIENT_CONSENT_SIGNATURE_FIELDS.includes(fieldName as FormField))
    );

    return Array.from(new Set([...metadataRequiredFieldNames, ...this.getConditionalRequiredFieldNames()]));
  };

  isFieldFilled = (fieldName: string): boolean => {
    const formField = this.props.formFieldMap.get(fieldName);
    if (!formField) {
      return false;
    }

    if (
      formField.inputType === FormFieldInputType.DROPDOWN ||
      formField.inputType === FormFieldInputType.MULTI_SELECT_RADIO ||
      formField.inputType === FormFieldInputType.RADIO_CHOICE
    ) {
      return Boolean(formField.chartContent?.some((item) => item.selected));
    }

    if (formField.inputType === FormFieldInputType.CHECK_BOX) {
      return Boolean(formField.chartContent?.some((item) => item.selected) || formField.value === true || formField.value === 'true');
    }

    const record = chartService.buildRecordFromField(formField);
    const value = record?.value ?? formField?.value ?? '';
    return String(value).trim().length > 0;
  };

  hasBlockingErrors = (): boolean => {
    return this.getRequiredFieldNames().some((fieldName) => {
      const field = this.props.formFieldMap.get(fieldName);
      return Boolean(field?.errors?.length);
    });
  };

  hasValidUnsavedChanges = (): boolean => {
    if (!this.props.hasUnsavedChanges) {
      return false;
    }

    if (this.hasBlockingErrors()) {
      return false;
    }

    return this.getRequiredFieldNames().every((fieldName) => this.isFieldFilled(fieldName));
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildFormFields());

  getFieldValue = (fieldName: FormField): string => {
    const formField = this.props.formFieldMap.get(fieldName);
    return String(chartService.buildRecordFromField(formField)?.value ?? '').trim();
  };

  getFieldLabel = (fieldName: FormField): string => {
    const formField = this.props.formFieldMap.get(fieldName);
    const selected = (formField?.chartContent || []).find((item) => !!item.value && item.selected);
    return selected?.label ?? '';
  };

  // Build a label-based record (dropdown/radio) only when a value is actually selected.
  buildLabelRecord = (formField: FormField, title: string) => {
    const content = this.getFieldLabel(formField);
    return content ? chartService.buildPlainRecord({ formField, title, content }) : null;
  };

  // Build a plain text/date/time record only when the field has a value.
  buildFieldRecord = (formField: FormField, title: string) => {
    const content = this.getFieldValue(formField);
    return content ? chartService.buildPlainRecord({ formField, title, content }) : null;
  };

  // Build a checkbox attestation record only when checked: the field label is the title and "True" is shown as the value.
  buildCheckboxRecord = (formField: FormField) => {
    const field = this.props.formFieldMap.get(formField);
    if (!field?.value) {
      return null;
    }
    const selected = (field.chartContent || []).find((item) => item.selected);
    const label = selected?.label ?? '';
    return label ? chartService.buildPlainRecord({ formField, title: label, content: 'True', value: true, contentId: selected?.id ?? '' }) : null;
  };

  buildFragment = (): ChartFragment => {
    const { buildPatientRecord } = chartService;

    const showDecisionMaker = this.shouldShowDecisionMakerFields();
    const showPatientConsent = this.shouldShowPatientConsent();

    const patientConsentRecords = [
      buildPatientRecord(this.props.formFieldMap, FormField.PATIENT_FULL_NAME),
      this.buildLabelRecord(FormField.DECISION_CAPACITY, FormFieldLabel.DECISION_CAPACITY),
      showDecisionMaker ? this.buildLabelRecord(FormField.DECISION_MAKER, FormFieldLabel.DECISION_MAKER) : null,
      showDecisionMaker ? buildPatientRecord(this.props.formFieldMap, FormField.DECISION_MAKER_NAME) : null,
      showDecisionMaker ? buildPatientRecord(this.props.formFieldMap, FormField.RELATIONSHIP_TO_PATIENT) : null,
      showPatientConsent ? this.buildCheckboxRecord(FormField.PATIENT_CONSENT_CHECKED) : null,
      showPatientConsent ? this.buildFieldRecord(FormField.PATIENT_CONSENT_SIGNATURE, FormFieldLabel.PATIENT_CONSENT_SIGNATURE) : null
    ].filter(Boolean);

    const providerOrderRecords = [
      this.buildCheckboxRecord(FormField.PROVIDER_ORDER_CHECKED),
      buildPatientRecord(this.props.formFieldMap, FormField.PROVIDER_NAME),
      this.buildLabelRecord(FormField.PROVIDER_TYPE, FormFieldLabel.PROVIDER_TYPE),
      buildPatientRecord(this.props.formFieldMap, FormField.EMERGENCY_PHONE),
      this.buildFieldRecord(FormField.PROVIDER_SIGNATURE, FormFieldLabel.PROVIDER_SIGNATURE)
    ].filter(Boolean);

    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.DO_NOT_RESUSCITATE,
      records: [
        {
          sectionTitle: SectionTitle.GENERAL_INFORMATION,
          records: [
            this.buildLabelRecord(FormField.STATE, FormFieldLabel.STATE),
            buildProviderNurseRecord(this.props.isAuthor, this.props.authorName, this.props.student)
          ].filter(Boolean)
        },
        {
          sectionTitle: SectionTitle.PATIENT_CONSENT,
          records: patientConsentRecords
        },
        {
          sectionTitle: SectionTitle.PROVIDER_ORDER,
          records: providerOrderRecords
        }
      ]
    };

    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return {
      ...basicInfo,
      chartData: record
    } as ChartFragment;
  };

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], { defaultFormFields: this.buildFormFields(), afterSave: this.props.navigateToSavedPatientCharting });
  };

  render() {
    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton: this.props.enableDisplayRecordsButton,
      enableSaveButton: this.hasValidUnsavedChanges(),
      saveButtonHasIcon: true,
      saveButtonText: 'Save & Complete',
      cancelButtonText: 'Discard Changes',
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: this.props.displayAuthoringData,
      onSaveClick: this.handleSaveClick
    };

    const { userRole, locale } = this.props;
    const isInstructorEditMode =
      userRole === UserRole.INSTRUCTOR && (assignmentHelper.isDemoEmptyEhr() || assignmentHelper.isInstructorAddDataToEhr(locale));

    const viewProps: DoNotResuscitateViewProps = {
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      showDecisionMakerFields: this.shouldShowDecisionMakerFields(),
      showPatientConsent: this.shouldShowPatientConsent(),
      isInstructorEditMode
    };

    return <DoNotResuscitateView {...viewProps} />;
  }
}

export { DoNotResuscitate as BaseDoNotResuscitate };

const mapStateToProps = (state) => ({
  authorName: studentSelectors.getAuthorName(state),
  userRole: appSelectors.getUserRole(state),
  locale: appSelectors.getLocale(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(DoNotResuscitate);
